import {ProcessFlowchartConfigEditorHeaderConfig} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorHeaderTypes';
import {
  LeftSidepanelContent,
  ProcessFlowchartMetadataEditorProps,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorLeftSidepanel';
import {warn} from 'util/Logging';

import {ProcessFlowchartNodeAdapter} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {FlowchartValidator} from './FlowchartValidator';
import {PLUGIN_REGISTRY_INSTANCE} from './PluginRegistry';

// Define the metadata editor in terms of the component, the props will be passed in at render time to form the content
export interface ProcessesPluginMetadataEditorItem extends Omit<LeftSidepanelContent, 'content'> {
  editorComponent: React.ComponentType<ProcessFlowchartMetadataEditorProps>;
}

/**
 * Base class for application team extensions to the Processes editor.
 *
 * Application teams subclass this base type to specify the extensions they're making available for use
 * with the editor. The abstract properties on this type specify different kinds of extensions.
 *
 * Clients wishing to use the Processes editor (see ProcessFlowchartConfigEditor) with extensions should
 * import the plugins containing those extensions, instantiate them, and call registerForEditor() to
 * make extensions provided by those plugins available to the editor. It's best to do this outside
 * the React component using the editor. For example:
 *
 * ```tsx
 * const workflowsPlugin = new DefaultProcessesPlugin();
 * workflowsPlugin.registerForEditor();
 *
 * const myPlugin = new MyPlugin();
 * myPlugin.registerForEditor();
 *
 * function MyComponentWithEditor(props: MyComponentWithEditorProps): JSX.Element {
 *   // The editor will now have access to the extensions provided by the loaded plugins.
 *   return <ProcessFlowchartConfigEditor .../>;
 * }
 * ```
 *
 * See this confluence page for more information on plugins: https://benchling.atlassian.net/wiki/spaces/Workflows/pages/1285619729/Adding+A+Processes+Plugin
 *
 * Default plugins are registered during test setup in client/test/lib/testSetup.ts. If you are adding a new plugin, you may need to update test setup accordingly.
 */
export abstract class ProcessesPluginBase {
  /**
   * A unique name for this plugin.
   */
  abstract readonly pluginName: string;

  /**
   * The flowchart type provided by this plugin.
   *
   * Flowchart type corresponds to a configurator that is used to configure the flowchart for a particular app domain.
   * Not all plugins must provide a flowchart type, some may only provide additional node adapters.
   */
  abstract readonly flowchartTypeName?: string;

  /**
   * The node adapters provided by this plugin.
   *
   * Node adapters tell the editor how to work with nodes for a particular task type, e.g. how to render
   * the node on the flowchart canvas and what sidepanel configuration component to show for the node.
   */
  abstract readonly nodeAdapters: Array<ProcessFlowchartNodeAdapter>;

  /**
   * The flowchart validator provided by this plugin.
   *
   * Flowchart validators are used to run flowchart-level validation for a particular type of flowchart / a particular app domain.
   * The generic type parameters allows plugins to specify their application-specific validation error, error type, and object type.
   */
  abstract readonly flowchartValidator: FlowchartValidator<object, string, string>;

  /**
   * The metadata editor provided by this plugin.
   *
   * Metadata editors are used to render a component for editing flowchart-level configuration/metadata for
   * the flowchart type delivered by this plugin.
   */
  abstract readonly metadataEditor?: ProcessesPluginMetadataEditorItem;

  /**
   * The static header configuration for the flowchart editor.
   *
   * Override this property to provide header configuration for the flowchart type.
   * Dynamic overrides (breadcrumb URL, flowchart name) are applied separately via
   * HeaderConfigMetadata passed to the header component.
   */
  readonly headerConfig?: ProcessFlowchartConfigEditorHeaderConfig;

  /**
   * Register this plugin and its extensions with the editor.
   *
   * Subclasses should never override this method.
   */
  registerForEditor(): void {
    if (!PLUGIN_REGISTRY_INSTANCE.hasPlugin(this.pluginName)) {
      PLUGIN_REGISTRY_INSTANCE.registerPluginAndItsExtensions(this);
    } else {
      warn(`Plugin ${this.pluginName} is already registered. Skipping registration.`);
    }
  }

  /**
   * Deregister this plugin and all of its extensions from the editor.
   *
   * This is meant to be used to clean up after tests - it is not intended to be used as part of production
   * code.
   *
   */
  deregisterFromEditor(): void {
    if (PLUGIN_REGISTRY_INSTANCE.hasPlugin(this.pluginName)) {
      PLUGIN_REGISTRY_INSTANCE.deregisterPluginAndItsExtensions(this);
    } else {
      warn(`Plugin ${this.pluginName} is not registered. Skipping deregistration.`);
    }
  }
}
